import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import type { VipNoticeType } from '@/api/auth'
import { useAppStore } from '@/hooks/useAppStore'

import {
  shouldShowVipNotice,
  vipNoticeDismissKey,
} from './vipExpiryNoticePolicy'

function messageKey(noticeType: VipNoticeType): string {
  return `vipNotice_${noticeType}`
}

export default function VipExpiryNotice() {
  const { user } = useAppStore()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const entitlement = user?.vipEntitlement
  const [visible, setVisible] = useState(false)

  const storageKey = useMemo(
    () => entitlement ? vipNoticeDismissKey(entitlement) : '',
    [entitlement],
  )

  useEffect(() => {
    const routeAllowsNotice = location.pathname === '/' || location.pathname === '/profile'
    setVisible(routeAllowsNotice && shouldShowVipNotice(entitlement))
  }, [entitlement, location.pathname, storageKey])

  if (!visible || !entitlement) return null

  const dismiss = () => {
    localStorage.setItem(storageKey, 'dismissed')
    setVisible(false)
  }

  return (
    <aside
      role="status"
      data-testid="vip-expiry-notice"
      className="fixed inset-x-4 top-[calc(env(safe-area-inset-top)+12px)] z-[80] mx-auto max-w-md rounded-2xl border border-amber-200 bg-white/95 p-4 shadow-xl backdrop-blur"
    >
      <button
        type="button"
        aria-label={t('vipNotice_dismiss')}
        onClick={dismiss}
        className="absolute right-3 top-3 h-8 w-8 rounded-full text-gray-400"
      >
        <i className="ri-close-line text-lg" />
      </button>
      <div className="flex gap-3 pr-8">
        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-amber-50 text-amber-500">
          <i className="ri-vip-crown-2-fill text-xl" />
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900">{t('vipNotice_title')}</p>
          <p className="mt-1 text-xs leading-5 text-gray-600">
            {t(messageKey(entitlement.notice_type), {
              days: entitlement.remaining_days,
              date: entitlement.expires_on,
              renewalDate: entitlement.renewal_at?.slice(0, 10) ?? entitlement.expires_on,
            })}
          </p>
          <button
            type="button"
            onClick={() => {
              dismiss()
              navigate('/membership')
            }}
            className="mt-2 text-xs font-semibold text-pink-500"
          >
            {t('vipNotice_viewMembership')}
          </button>
        </div>
      </div>
    </aside>
  )
}
